import * as React from 'react';
import * as S from './styles';
import { ReactComponent as ExIcon } from 'common/images/close-icon.svg';
import RadioButtonIcon from 'common/icons/radioButtonIcon';
import { validateHourFormat, validateOnlyNumbers } from 'features/Dealers/Listings/Data/state/utils';
import { SpecialHourFormat } from 'features/Dealers/Listings/SpecialHours';
import * as _ from 'lodash';

export interface EditSpecialHoursProps {
  specialHoursState?: SpecialHourFormat[];
  setSpecialHoursState: Function;
}

const initSpecialHour: SpecialHourFormat = {
  openTime: "",
  closeTime: "",
  isClosed: false,
  is24hrs: false,
  format: {month: "", day: "", year: ""}
}

const EditSpecialHours: React.FC<EditSpecialHoursProps> = (props) => {

  const [newSpecialHour, setNewSpecialHour] = React.useState<SpecialHourFormat>(_.cloneDeep(initSpecialHour));

  React.useEffect(() => {
    if (props.specialHoursState?.length) {
      let newSpecialHoursState = [...props.specialHoursState];
      newSpecialHoursState.map((specialHour, index) => {
        let month = "";
        let day = "";
        let year = "";
        if (specialHour.startDate) {
          const objDate = new Date(specialHour.startDate);
          month = (objDate.getUTCMonth() + 1).toString(); //months from 1-12
          day = objDate.getUTCDate().toString();
          year = objDate.getUTCFullYear().toString();
        }
        newSpecialHoursState[index].format = {month, day, year};
      })
    }
  }, []);

  const onInputChange = (specialHour: SpecialHourFormat, e: React.ChangeEvent<HTMLInputElement>, field: string, index?: number) => {
    const value = e.target.value;
    if (field === "month" || field === "day" || field === "year") {
      let format = specialHour.format ?? {month: "", day: "", year: ""};
      let numberValue = parseInt(value);
      if (field === "month") {
        if (value.length < 3) {
          if (numberValue > 12) return `12`;
          else format.month = value;
        };
      } else if (field === "day") {
        if (value.length < 3) {
          if (numberValue > 31) return `31`;
          else format.day = value;
        }
      } else if (field === "year") {
        if (value.length < 5) format.year = value;
      }
      specialHour.format = format;
      updateStateSpecialHour(specialHour, index);
    } else {
      if (value.length < 6) {
        if (field === "openTime") {
          specialHour.openTime = value;
        } else if (field === "closeTime") {
          specialHour.closeTime = value;
        }
      }
      updateStateSpecialHour(specialHour, index);
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>, field: string) => {
    if (field === "month" || field === "day" || field === "year") validateOnlyNumbers(e);
    else validateHourFormat(e);
  }

  const handleCustomHours = (specialHour: SpecialHourFormat, index?: number) => {
    specialHour.is24hrs = false;
    specialHour.isClosed = false;
    updateStateSpecialHour(specialHour, index);
  }

  const changeRadioOptions = (specialHour: SpecialHourFormat, field: string, value: boolean, index?: number) => {
    if (value === true) {
      if (field === "is24hrs") {
        specialHour.is24hrs = value;
        specialHour.isClosed = false;
      } else {
        specialHour.isClosed = value;
        specialHour.is24hrs = false;
      }
    }
    updateStateSpecialHour(specialHour, index);
  }

  const addSpecialHour = () => {
    if (newSpecialHour.format?.month && newSpecialHour.format?.day && newSpecialHour.format?.year) {
      const addSpecialHour = _.cloneDeep(newSpecialHour);
      if (props.specialHoursState?.length) {
        props.setSpecialHoursState([
          ...props.specialHoursState,
          addSpecialHour
        ]);
      } else {
        props.setSpecialHoursState([addSpecialHour]);
      }
      setNewSpecialHour(_.cloneDeep(initSpecialHour));
    }
  }

  const removeSpecialHour = (index: number) => {
    if (props.specialHoursState?.length) {
      props.setSpecialHoursState([
        ...props.specialHoursState.slice(0, index),
        ...props.specialHoursState.slice(index + 1)
      ]);
    }
  }

  const updateStateSpecialHour = (specialHour: SpecialHourFormat, index) => {
    if (index !== undefined && props.specialHoursState?.length) {
      props.setSpecialHoursState([
        ...props.specialHoursState.slice(0, index),
        {...specialHour},
        ...props.specialHoursState.slice(index + 1)
      ]);
    } else {
      setNewSpecialHour({...specialHour});
    }
  }

  const renderRadioButton = (selected: boolean) => {
    let radioStyles: any = {fillColor: '#FFFFFF00', strokeColor: '#a5a5a5'};
    if (selected) radioStyles = {fillColor: undefined, strokeColor: undefined}
    return <RadioButtonIcon {...radioStyles} />
  }

  const renderInputField = (specialHour: SpecialHourFormat, field: string, value: string, placeholder?: string,  index?: number, disabled?: boolean) => {
    return (
      <S.EditSHInput
        backgroundColorValue={disabled ? 'rgba(189, 189, 189, 0.2)' : undefined}
        id={`${field}-${index}`}
        value={disabled ? "" : value.toString()}
        placeholder={disabled ? "" : placeholder}
        readOnly={disabled ? true : undefined}
        disabled={disabled}
        onChange={(e) => onInputChange(specialHour, e, field, index)}
        onKeyPress={(e) => handleKeyPress(e, field)}
      />
    )
  }

  const renderSpecialHourRow = (specialHour: SpecialHourFormat, index?: number) => {
    const disableHours = specialHour.is24hrs || specialHour.isClosed;
    return (
      <S.EditSHRowContainer key={index}>
        <S.EditSHRowDate>
          <S.EditSHRowTitle>
            Date
          </S.EditSHRowTitle>
          <S.EditSHDateFields>
            {renderInputField(specialHour, "month", specialHour.format?.month ?? "", "mm", index)}
            {renderInputField(specialHour, "day", specialHour.format?.day ?? "", "dd", index)}
            {renderInputField(specialHour, "year", specialHour.format?.year ?? "", "yyyy", index)}
          </S.EditSHDateFields>
          <S.EditSHRadioButtonsContainer>
            <S.EditSHRadioButton
              onClick={() => changeRadioOptions(specialHour, "is24hrs", !specialHour.is24hrs, index)}
            >
              {renderRadioButton(specialHour.is24hrs)}
            </S.EditSHRadioButton>
            <S.EditSHRadioLabel>
              24hr
            </S.EditSHRadioLabel>
            <S.EditSHRadioButton
              onClick={() => changeRadioOptions(specialHour, "isClosed", !specialHour.isClosed, index)}
            >
              {renderRadioButton(specialHour.isClosed)}
            </S.EditSHRadioButton>
            <S.EditSHRadioLabel>
              Closed
            </S.EditSHRadioLabel>
          </S.EditSHRadioButtonsContainer>
        </S.EditSHRowDate>
        <S.EditSHRowHours>
          <S.EditSHRowTitle>
            Hours
          </S.EditSHRowTitle>
          <S.EditSHRowHoursFlex>
            <S.EditSHHoursFields
              onDoubleClick={() => handleCustomHours(specialHour, index)}
            >
              {renderInputField(specialHour, "openTime", specialHour.openTime, "open", index, disableHours)}
              {renderInputField(specialHour, "closeTime", specialHour.closeTime, "close", index, disableHours)}
            </S.EditSHHoursFields>
            <S.EditSHActionsContainer>
              {
                index === undefined ? 
                  <S.EditSHActionAdd
                    onClick={addSpecialHour}
                  >
                    Add
                  </S.EditSHActionAdd>
                :
                  <S.EditSHActionClose
                    onClick={() => removeSpecialHour(index)}
                  >
                    <ExIcon />
                  </S.EditSHActionClose>
              }
            </S.EditSHActionsContainer>
          </S.EditSHRowHoursFlex>
        </S.EditSHRowHours>
      </S.EditSHRowContainer>
    );
  }

  return (
    <S.EditSpecialHoursContainer>
      { renderSpecialHourRow(newSpecialHour) }
      <S.EditSHDividerContainer>
        <S.EditSHDivider />
      </S.EditSHDividerContainer>
      <S.EditSHRowsContainer>
        {
          
          (props.specialHoursState?.length && props.specialHoursState.length > 0) ?
            props.specialHoursState.map((specialHour, index) => {
              return renderSpecialHourRow(specialHour, index);
            })
          :
            <S.EditSHEmptyState>
              Add special hours to view them here
            </S.EditSHEmptyState>
        }
      </S.EditSHRowsContainer>
    </S.EditSpecialHoursContainer>
  )
}

export default EditSpecialHours